/**
 * EventCheckInUniversal — Phase 4 (iter-434) enhanced scanner.
 *
 * What's new vs Phase 2:
 *   • Offline queue (Dexie/IndexedDB). Scans made offline are queued
 *     and drained via POST /api/qr/bulk-sync as soon as connectivity
 *     returns. Idempotent by client_scan_id.
 *   • VIP header + fanfare chime when delegate.vip_tier is set.
 *   • Distinct audible verdicts (green / amber / red) — WebAudio, no
 *     bundled MP3s.
 *   • Connectivity + queue-depth chips in the header.
 *   • Manual "Flush queue" button + auto-flush on `online` event.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ScanLine, ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Camera,
  Users, TrendingUp, Activity, Wifi, WifiOff, RefreshCw, Crown,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import getScanQueue from '@/lib/scanQueue';
import { playScanSound } from '@/lib/scanSounds';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const VERDICT_STYLES = {
  green:  { icon: CheckCircle2,  ring: 'ring-emerald-400/60', bg: 'bg-emerald-500/15', text: 'text-emerald-200' },
  amber:  { icon: AlertTriangle, ring: 'ring-amber-400/60',   bg: 'bg-amber-500/15',   text: 'text-amber-200' },
  red:    { icon: XCircle,        ring: 'ring-rose-400/60',    bg: 'bg-rose-500/15',    text: 'text-rose-200' },
};

// VIP tier → coloured banner. Explicit palette (no dynamic tailwind classes).
const VIP_STYLES = {
  platinum: { ring: 'ring-slate-200/80',  bg: 'bg-gradient-to-r from-slate-400/40 to-slate-200/40',    label: 'PLATINUM VIP' },
  diamond:  { ring: 'ring-cyan-200/80',   bg: 'bg-gradient-to-r from-cyan-500/40 to-sky-300/40',       label: 'DIAMOND VIP' },
  gold:     { ring: 'ring-yellow-200/80', bg: 'bg-gradient-to-r from-yellow-500/40 to-amber-300/40',   label: 'GOLD VIP' },
  silver:   { ring: 'ring-slate-300/80',  bg: 'bg-gradient-to-r from-slate-500/40 to-slate-300/40',    label: 'SILVER VIP' },
  vip:      { ring: 'ring-fuchsia-200/80', bg: 'bg-gradient-to-r from-fuchsia-500/40 to-pink-400/40',   label: 'VIP' },
};

const EventCheckInUniversal = () => {
  const { eventId } = useParams();
  const qc = useQueryClient();
  const [qr, setQr] = useState('');
  const [gate, setGate] = useState('main');
  const [lastResult, setLastResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [queueDepth, setQueueDepth] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const inputRef = useRef(null);
  const scanQueue = useRef(getScanQueue()).current;

  useEffect(() => { inputRef.current?.focus(); }, [lastResult]);

  const refreshQueueDepth = useCallback(async () => {
    try { setQueueDepth(await scanQueue.count()); } catch { /* ignore */ }
  }, [scanQueue]);

  useEffect(() => { refreshQueueDepth(); }, [refreshQueueDepth]);

  const { data: stats } = useQuery({
    queryKey: ['checkin-stats', eventId],
    queryFn: async () => (await axios.get(
      `${API}/api/events/${eventId}/checkin/stats`, { headers: authH() }
    )).data,
    refetchInterval: online ? 5000 : false,
    enabled: !!eventId && online,
  });

  // ---------------- online/offline listeners + auto-flush ----------------
  const flushQueue = useCallback(async () => {
    if (flushing) return;
    setFlushing(true);
    try {
      const res = await scanQueue.flush(API, authH());
      if (res.attempted > 0) {
        const parts = [];
        if (res.applied)   parts.push(`${res.applied} applied`);
        if (res.duplicate) parts.push(`${res.duplicate} duplicate`);
        if (res.failed)    parts.push(`${res.failed} failed`);
        toast.success(`Synced ${res.attempted} queued scan${res.attempted === 1 ? '' : 's'} · ${parts.join(' · ')}`);
        qc.invalidateQueries({ queryKey: ['checkin-stats', eventId] });
      }
    } catch (e) {
      toast.error('Sync failed: ' + (e?.message || 'unknown error'));
    } finally {
      setFlushing(false);
      refreshQueueDepth();
    }
  }, [scanQueue, flushing, qc, eventId, refreshQueueDepth]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flushQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [flushQueue]);

  // Attempt a flush once on mount too (covers "the app was left open all night
  // and network came back at 3am" case)
  useEffect(() => {
    if (online) { flushQueue(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- scan submit ----------------
  const submit = async (raw) => {
    const value = (raw ?? qr).trim();
    if (!value || scanning) return;
    setScanning(true);

    // Offline branch — enqueue only
    if (!navigator.onLine) {
      try {
        await scanQueue.add({ event_id: eventId, qr: value, gate });
        const optimistic = {
          verdict: 'amber', reason: 'Queued offline — will sync when online',
          delegate: null, queued: true,
        };
        setLastResult(optimistic);
        playScanSound('amber');
        toast.warning('Queued offline · will sync when back online');
        setQr('');
        refreshQueueDepth();
      } catch (e) {
        toast.error('Queue failed: ' + (e?.message || e));
      } finally {
        setScanning(false);
      }
      return;
    }

    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/checkin/scan`,
        { qr: value, gate },
        { headers: authH() }
      );
      setLastResult(res.data);
      const v = res.data.verdict;
      const vip = !!res.data.delegate?.vip_tier;
      playScanSound(v, { vip: v === 'green' && vip });
      if (v === 'green') toast.success(`Welcome ${res.data.delegate?.name || ''}`);
      else if (v === 'amber') toast.warning(res.data.reason || 'Already checked in');
      else toast.error(res.data.reason || 'Scan failed');
      qc.invalidateQueries({ queryKey: ['checkin-stats', eventId] });
      setQr('');
    } catch (e) {
      // Network failure while thought-to-be-online → queue as fallback
      if (e?.message === 'Network Error' || !navigator.onLine) {
        await scanQueue.add({ event_id: eventId, qr: value, gate });
        setLastResult({ verdict: 'amber',
                          reason: 'Network dropped mid-scan — queued locally', queued: true });
        playScanSound('amber');
        toast.warning('Queued (network dropped)');
        setOnline(false);
        refreshQueueDepth();
      } else {
        toast.error(e?.response?.data?.detail || e.message || 'Scan failed');
      }
    } finally {
      setScanning(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const verdict = lastResult?.verdict;
  const Style = verdict ? VERDICT_STYLES[verdict] : null;
  const vipMeta = lastResult?.delegate?.vip_tier && verdict === 'green'
    ? (VIP_STYLES[lastResult.delegate.vip_tier] || VIP_STYLES.vip)
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <Link to={`/admin/event/${eventId}`}
              className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to event
        </Link>

        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <ScanLine className="h-6 w-6 text-indigo-400" /> Universal Event Check-in
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Scan any FWM QR — universal, legacy delegate, badge code or plain ID — to check in delegates.
            </p>
          </div>
          {/* Connectivity + queue chip */}
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={online
                ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10'
                : 'border-amber-400/40 text-amber-300 bg-amber-500/10'}
              data-testid="scanner-connectivity-chip"
            >
              {online ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
              {online ? 'Online' : 'Offline'}
            </Badge>
            <Badge
              variant="outline"
              className={queueDepth > 0
                ? 'border-amber-400/40 text-amber-300 bg-amber-500/10'
                : 'border-slate-700 text-slate-400 bg-slate-800/40'}
              data-testid="scanner-queue-depth-chip"
            >
              Queue: {queueDepth}
            </Badge>
            <Button
              size="sm" variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={flushQueue}
              disabled={flushing || queueDepth === 0 || !online}
              data-testid="scanner-flush-btn"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${flushing ? 'animate-spin' : ''}`} />
              {flushing ? 'Syncing…' : 'Sync queue'}
            </Button>
          </div>
        </header>

        {/* Live stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="checkin-stats">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Registered</div>
              <div className="text-3xl font-semibold text-white">{stats?.registered ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Checked-in</div>
              <div className="text-3xl font-semibold text-emerald-300">{stats?.checked_in ?? '—'}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Attendance</div>
              <div className="text-3xl font-semibold text-indigo-300">{stats ? `${stats.percentage}%` : '—'}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Scans · 24h</div>
              <div className="text-3xl font-semibold text-amber-300">{stats?.last_24h_scans ?? '—'}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Scanner */}
          <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Camera className="h-4 w-4" /> Scan / Type / Paste a QR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={qr}
                  onChange={e => setQr(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Paste/scan QR here — or wire a USB scanner; press Enter"
                  className="bg-slate-900 border-slate-800 font-mono text-sm"
                  data-testid="checkin-qr-input"
                />
                <Input value={gate} onChange={e => setGate(e.target.value)}
                       placeholder="Gate"
                       className="w-28 bg-slate-900 border-slate-800"
                       data-testid="checkin-gate" />
                <Button onClick={() => submit()} disabled={scanning || !qr.trim()}
                         data-testid="checkin-submit-btn">
                  {scanning ? 'Scanning…' : 'Check-in'}
                </Button>
              </div>

              {/* VIP banner — pops up above the standard verdict card */}
              {vipMeta && (
                <div className={`rounded-xl ring-2 ${vipMeta.ring} ${vipMeta.bg} p-4 flex items-center gap-3 text-white shadow-lg`}
                     data-testid="scanner-vip-banner">
                  <Crown className="h-8 w-8 text-yellow-100 drop-shadow" />
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest opacity-80">{vipMeta.label}</div>
                    <div className="text-xl font-semibold">
                      {lastResult.delegate?.name}
                    </div>
                  </div>
                </div>
              )}

              {Style && (
                <div className={`rounded-xl ring-2 ${Style.ring} ${Style.bg} p-5 ${Style.text}`}
                     data-testid="checkin-result">
                  <div className="flex items-start gap-3">
                    <Style.icon className="h-10 w-10 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-lg font-semibold">
                        {lastResult.reason}
                        {lastResult.queued && <span className="ml-2 text-xs opacity-70">(offline)</span>}
                      </div>
                      {lastResult.delegate && (
                        <div className="mt-2 space-y-1 text-sm">
                          <div className="text-2xl font-medium flex items-center gap-2">
                            {lastResult.delegate.photo_url && (
                              <img src={lastResult.delegate.photo_url} alt=""
                                     className="h-10 w-10 rounded-full ring-2 ring-white/30 object-cover" />
                            )}
                            {lastResult.delegate.name}
                          </div>
                          <div className="text-xs opacity-80 flex gap-3 flex-wrap">
                            {lastResult.delegate.badge_code && (
                              <span>Badge: <strong>{lastResult.delegate.badge_code}</strong></span>
                            )}
                            {lastResult.delegate.tier_name && (
                              <span>Tier: <strong>{lastResult.delegate.tier_name}</strong></span>
                            )}
                            {lastResult.gate && <span>Gate: <strong>{lastResult.gate}</strong></span>}
                          </div>
                          <div className="text-xs opacity-70 flex gap-3 mt-2">
                            {lastResult.delegate.meals_included && <Badge variant="outline" className="bg-emerald-500/10 border-emerald-400/30 text-emerald-200">Meals</Badge>}
                            {lastResult.delegate.housing_included && <Badge variant="outline" className="bg-sky-500/10 border-sky-400/30 text-sky-200">Housing</Badge>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!Style && (
                <div className="rounded-xl border border-dashed border-slate-800 p-6 text-center text-sm text-slate-500">
                  Awaiting first scan. Verdict colours: GREEN = welcome · AMBER = already checked-in · RED = invalid / wrong event.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent scans */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4" /> Last 20 scans
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[500px] overflow-y-auto"
                          data-testid="checkin-recent">
              {(stats?.recent || []).length === 0 && (
                <p className="text-xs text-slate-500">No scans yet.</p>
              )}
              {(stats?.recent || []).map(s => (
                <div key={s.id} className="border border-slate-800 rounded-md p-2 bg-slate-800/30 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-200 font-medium">{s.delegate_name}</span>
                    <Badge variant="outline" className={`text-[10px] ${
                      s.verdict === 'green' ? 'border-emerald-400/40 text-emerald-300'
                        : s.verdict === 'amber' ? 'border-amber-400/40 text-amber-300'
                          : 'border-rose-400/40 text-rose-300'
                    }`}>{s.verdict}</Badge>
                  </div>
                  <div className="text-slate-500 mt-0.5">
                    {s.tier_name} · {s.gate} · {s.scanned_at ? new Date(s.scanned_at).toLocaleTimeString() : ''}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventCheckInUniversal;
